import React from "react";
import { Box } from "braid-design-system";
import Loading from "../components/Loading";
import ItemGroupListTable from "../components/ItemGroup/ItemGroupListTable";
import CreateUpdateItemGroupDrawer, { ItemGroupFormData, ItemGroupToEdit } from "../components/ItemGroup/CreateUpdateItemGroupDrawer";
import { createItemGroup, getAllItemGroups, updateItemGroup, ItemGroupCreateUpdateRequest, ItemGroupResponse } from "../services/itemGroupService";
import { useToast } from "../hooks/useToast";
import { useCanManageMasters } from "../hooks/useCanManageMasters";

const toRequestPayload = (formData: ItemGroupFormData): ItemGroupCreateUpdateRequest => ({
    group_code: formData.group_code,
    group_name: formData.group_name,
    group_icon: formData.group_icon.trim() || null,
    group_image: formData.group_image.trim() || null,
    group_saccno: formData.group_saccno,
    group_caccno: formData.group_caccno,
    group_iaccno: formData.group_iaccno,
    group_prchadays: formData.group_prchadays === "" ? null : Number(formData.group_prchadays),
    group_reorder: formData.group_reorder === "" ? null : Number(formData.group_reorder),
});

const ItemGroupManagement: React.FC = () => {
    const toast = useToast();
    const canManageMasters = useCanManageMasters();
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const [itemGroupList, setItemGroupList] = React.useState<ItemGroupResponse[]>([]);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedItemGroup, setSelectedItemGroup] = React.useState<ItemGroupToEdit | undefined>(undefined);

    const fetchItemGroups = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingMessage("Please wait, Fetching item groups...");
            const data = await getAllItemGroups();
            setItemGroupList(data);
        } catch (error: any) {
            toast.showError(`Error fetching item groups: ${error?.message || "Unknown error"}`);
            setItemGroupList([]);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [toast]);

    React.useEffect(() => {
        fetchItemGroups();
    }, [fetchItemGroups]);

    const handleSaveItemGroup = React.useCallback(async (groupData: ItemGroupFormData, editingGroup?: ItemGroupToEdit) => {
        const payload = toRequestPayload(groupData);
        if (editingGroup?.group_id) {
            await updateItemGroup(editingGroup.group_id, payload);
        } else {
            await createItemGroup(payload);
        }
    }, []);

    const handleEditItemGroup = React.useCallback((group: ItemGroupResponse) => {
        setSelectedItemGroup({
            ...group,
            group_id: group.group_id ?? undefined,
            group_prchadays: group.group_prchadays != null ? String(group.group_prchadays) : "",
            group_reorder: group.group_reorder != null ? String(group.group_reorder) : "",
        } as ItemGroupToEdit);
        setDrawerOpen(true);
    }, []);

    const handleCloseDrawer = React.useCallback(() => {
        setDrawerOpen(false);
        setSelectedItemGroup(undefined);
    }, []);

    return (
        <>
            {loading && <Loading message={loadingMessage || "Loading..."} />}

            <Box padding="medium">
                <ItemGroupListTable
                    itemGroupList={itemGroupList}
                    onCreateItemGroup={canManageMasters ? () => setDrawerOpen(true) : undefined}
                    onEditItemGroup={handleEditItemGroup}
                />
            </Box>

            <CreateUpdateItemGroupDrawer
                isOpen={drawerOpen}
                onClose={handleCloseDrawer}
                itemGroupToEdit={selectedItemGroup}
                onSave={handleSaveItemGroup}
                onItemGroupSaved={fetchItemGroups}
                isReadOnly={!canManageMasters}
            />
        </>
    );
};

export default ItemGroupManagement;
